/**
 * RTWO Native Currency Engine — Fee Policy Service
 */

import type { CurrencyId, FeePolicyId, RTWOFeePolicy } from '../domain-models';
import type { FeePolicyResult, IFeePolicyService } from '../service-interfaces';
import type { NATVRepositories } from '../repositories/natv-repositories';
import type { ProtocolAdapterRegistry } from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

export class FeePolicyService extends BaseNATVService implements IFeePolicyService {
  readonly serviceId = 'natv-fee-policy-001';
  readonly serviceName = 'Fee Policy Service';
  readonly description =
    'Fee policy service — placeholder, requires Genesis Configuration and Protocol Engine. No fake fee values.';
  readonly integrations = [
    'Core Engine',
    'Blockchain Protocol Engine',
    'Validator Infrastructure',
    'Governance (future)',
  ];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = false;

  constructor(
    context: NATVServiceContext,
    private readonly repositories: NATVRepositories,
    private readonly protocolRegistry: ProtocolAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_genesis_configuration';
  }

  async configureFeePolicy(
    currencyId: CurrencyId,
    baseFeePolicy: string,
    priorityFeePolicy: string,
    burnFeePolicy: string,
    validatorFeePolicy: string
  ): Promise<FeePolicyResult> {
    if (!this.protocolRegistry.isConfigured()) {
      return {
        success: false,
        feePolicyId: null,
        error:
          'Fee policy configuration requires Genesis Configuration — Fee Policy Not Configured',
        isPlaceholder: true,
        source: 'Native Currency Engine — Fee Policy Not Configured',
      };
    }

    const adapterResult = await this.protocolRegistry
      .getActiveAdapter()
      .configureFeePolicy(
        currencyId,
        baseFeePolicy,
        priorityFeePolicy,
        burnFeePolicy,
        validatorFeePolicy
      );

    if (adapterResult?.feePolicy) {
      this.repositories.feePolicies.save(adapterResult.feePolicy);
      this.context.eventBus.emit(NATVInternalEventTypes.FEE_POLICY_CONFIGURED, {
        feePolicyId: adapterResult.feePolicy.feePolicyId,
        currencyId,
      });
    }

    return {
      success: false,
      feePolicyId: null,
      error: 'Fee policy configuration requires Genesis Configuration — Fee Policy Not Configured',
      isPlaceholder: true,
      source: 'Native Currency Engine — Fee Policy Not Configured',
    };
  }

  getFeePolicyByCurrencyId(currencyId: CurrencyId): RTWOFeePolicy | null {
    if (!this.protocolRegistry.isConfigured()) return null;
    return this.repositories.feePolicies.findByCurrencyId(currencyId);
  }

  getFeePolicy(feePolicyId: FeePolicyId): RTWOFeePolicy | null {
    if (!this.protocolRegistry.isConfigured()) return null;
    return this.repositories.feePolicies.findById(feePolicyId);
  }

  getAllFeePolicies(): RTWOFeePolicy[] {
    if (!this.protocolRegistry.isConfigured()) return [];
    return this.repositories.feePolicies.findAll();
  }
}
